/**
 * Loyalty Points Animation Library
 *
 * Handles various animations and number counter animations for loyalty points transitions
 */

(function () {
	'use strict';

	/**
	 * Canvas-based Fireworks Renderer
	 * Adapted from canvas fireworks for widget containers
	 */
	class CanvasFireworksRenderer {
		constructor(canvas, container, duration = 0, parentAnimation = null) {
			this.canvas = canvas;
			this.ctx = canvas.getContext('2d');
			this.container = container;
			this.particles = [];
			this.animationId = null;
			this.isActive = false;
			this.isWindingDown = false;
			this.lastLaunch = 0;
			this.launchInterval = 2000; // 2 seconds between launches
			this.duration = duration * 1000; // Convert seconds to milliseconds
			this.startTime = 0;
			this.parentAnimation = parentAnimation;

			this.setupCanvas();
			this.bindEvents();
		}

		setupCanvas() {
			const rect = this.container.getBoundingClientRect();
			this.canvas.width = rect.width;
			this.canvas.height = rect.height;
		}

		bindEvents() {
			// Handle container resize
			window.addEventListener('resize', () => {
				this.setupCanvas();
			});
		}

		start() {
			this.isActive = true;
			this.startTime = Date.now();
			this.animate();
			// Launch initial firework
			setTimeout(() => this.launchFirework(), 500);
		}

		stop() {
			this.isActive = false;
			this.isWindingDown = false;
			if (this.animationId) {
				cancelAnimationFrame(this.animationId);
				this.animationId = null;
			}
			// Clear particles
			this.particles = [];
			this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

			// Stop the parent animation when canvas animation stops (avoid recursion)
			if (this.parentAnimation && !this.parentAnimation.isStopping) {
				this.parentAnimation.stopAnimation();
			}
		}

		launchFirework() {
			if (!this.isActive) return;

			const x = Math.random() * (this.canvas.width * 0.8) + (this.canvas.width * 0.1);
			const y = this.canvas.height;
			const color = `hsl(${Math.random() * 360}, 100%, 50%)`;

			this.particles.push(new FireworkParticle(x, y, color, true));
		}

		createExplosion(x, y, color) {
			// Reduced particle count for more subtle explosions (80% reduction)
			const isMobile = window.innerWidth <= 768;
			const particleCount = isMobile ? 14 : 26; // Reduced for subtler visual impact
			for (let i = 0; i < particleCount; i++) {
				this.particles.push(new FireworkParticle(x, y, color, false));
			}
		}

		animate() {
			if (!this.isActive) {
				return;
			}

			// Check if duration has been exceeded (0 = unlimited)
			if (this.duration > 0 && Date.now() - this.startTime > this.duration && !this.isWindingDown) {
				// Enter winding down mode - stop creating new particles but let existing ones finish
				this.isWindingDown = true;

				// Force any remaining rockets to explode immediately when entering wind-down
				for (let i = this.particles.length - 1; i >= 0; i--) {
					const p = this.particles[i];
					if (p.isRocket) {
						this.createExplosion(p.x, p.y, p.color);
						this.particles.splice(i, 1);
					}
				}
			}

			// If winding down and no particles left, stop completely
			if (this.isWindingDown && this.particles.length === 0) {
				this.stop();
				return;
			}

			// Use composite operation for trail effects that preserves transparency
			// This creates trails without darkening the background
			this.ctx.globalCompositeOperation = 'destination-out';
			this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
			this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
			this.ctx.globalCompositeOperation = 'source-over';

			// Update and draw particles
			for (let i = this.particles.length - 1; i >= 0; i--) {
				const p = this.particles[i];
				p.update();
				p.draw(this.ctx);

				// If rocket reaches apex, explode it
				if (p.isRocket && p.vy >= 0) {
					this.createExplosion(p.x, p.y, p.color);
					this.particles.splice(i, 1);
				}
				// Remove faded explosion particles
				else if (!p.isRocket && p.alpha <= 0) {
					this.particles.splice(i, 1);
				}
			}

			// Launch fireworks periodically (only if not winding down)
			const now = Date.now();
			if (!this.isWindingDown && now - this.lastLaunch > this.launchInterval) {
				this.launchFirework();
				this.lastLaunch = now;
			}

			this.animationId = requestAnimationFrame(() => this.animate());
		}
	}

	/**
	 * Firework Particle class for canvas rendering
	 */
	class FireworkParticle {
		constructor(x, y, color, isRocket) {
			this.x = x;
			this.y = y;
			this.color = color;
			this.isRocket = isRocket;
			this.alpha = 1;
			this.gravity = 0.1;
			this.drag = 0.98;

			if (this.isRocket) {
				// Rocket properties
				this.size = 2.5;
				this.vx = Math.random() * 4 - 2;
				this.vy = -(Math.random() * 7 + 12);
			} else {
				// Explosion particle properties with size variation
				this.size = Math.random() * 2.5 + 1.5; // 1.5-4px for varied explosion particles
				const angle = Math.random() * Math.PI * 2;
				const speed = Math.random() * 10 + 2;
				this.vx = Math.cos(angle) * speed;
				this.vy = Math.sin(angle) * speed;
				this.lifespan = Math.random() * 60 + 80; // Frames to live
			}
		}

		update() {
			// Apply physics
			this.x += this.vx;
			this.y += this.vy;
			this.vy += this.gravity;
			this.vx *= this.drag;
			this.vy *= this.drag;

			// Update lifespan for explosion particles
			if (!this.isRocket) {
				this.lifespan--;
				if (this.lifespan <= 0) {
					// Faster fade out - 0.1 instead of 0.05
					this.alpha = Math.max(0, this.alpha - 0.1);
				}
			}
		}

		draw(ctx) {
			ctx.save();
			ctx.globalAlpha = this.alpha;
			ctx.fillStyle = this.color;
			ctx.beginPath();
			ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
			ctx.fill();
			ctx.restore();
		}
	}

	class LoyaltyPointsAnimation {
		constructor() {
			this.isAnimating = false;
			this.isWindingDown = false;
			this.particles = [];
			this.animationId = null;
			this.particleSpawnInterval = null;
			this.cleanupInterval = null;
			this.maxActiveParticles = 150;
			this.particleSpawnRate = 150; // milliseconds between spawns
			this.animationType = 'confetti';
			this.canvasRenderer = null;
			this.animationStartTime = 0;
		}

		/**
		 * Initialize animation for a loyalty points widget
		 * @param {HTMLElement} element - The widget container element
		 * @param {Object} options - Animation options
		 */
		init(element, options = {}) {
			if (!element) return;

			const defaults = {
				targetPoints: 0,
				animationType: 'confetti',
				counterDuration: 2000,
				continuousAnimation: true,
				successMessage: '',
				animationDuration: 0
			};

			this.options = {...defaults, ...options};
			this.element = element;
			this.animationType = this.options.animationType;
			this.pointsElement = element.querySelector('.agend-loyalty-points-available-points-counter');
			this.animationContainer = element.querySelector('.animation-container');
			this.animationStartTime = Date.now();

			// Start animation based on type
			if (this.animationType !== 'none' && this.animationContainer) {
				if (this.options.continuousAnimation) {
					this.startContinuousAnimation();
				} else {
					this.startAnimation();
				}
			}

			this.animateCounter();
		}

		/**
		 * Animate the points counter from 0 to target value
		 */
		animateCounter() {
			if (!this.pointsElement) return;

			const targetPoints = this.options.targetPoints;
			const duration = this.options.counterDuration;
			const startTime = performance.now();

			const animate = (currentTime) => {
				const elapsed = currentTime - startTime;
				const progress = Math.min(elapsed / duration, 1);

				// Easing function (ease-out)
				const easeOut = 1 - Math.pow(1 - progress, 3);
				const currentValue = Math.floor(targetPoints * easeOut);

				// Update the display
				this.pointsElement.textContent = this.formatNumber(currentValue);

				if (progress < 1) {
					requestAnimationFrame(animate);
				} else {
					// Animation complete
					this.pointsElement.textContent = this.formatNumber(targetPoints);
				}
			};

			requestAnimationFrame(animate);
		}

		/**
		 * Format number with commas
		 * @param {number} num - Number to format
		 * @returns {string} Formatted number
		 */
		formatNumber(num) {
			return num.toLocaleString();
		}

		/**
		 * Start animation (legacy method for backward compatibility)
		 */
		startAnimation() {
			if (!this.animationContainer) return;

			this.isAnimating = true;
			this.createParticles();
			this.animate();

			// Stop animation after 4 seconds (legacy behavior)
			setTimeout(() => {
				this.stopAnimation();
			}, 4000);
		}

		/**
		 * Start continuous animation
		 */
		startContinuousAnimation() {
			if (!this.animationContainer) return;

			this.isAnimating = true;

			// Initial batch of particles
			this.spawnParticlesBatch();

			// Start continuous particle spawning
			this.particleSpawnInterval = setInterval(() => {
				if (this.isAnimating && !this.isWindingDown && this.particles.length < this.maxActiveParticles) {
					this.spawnParticle();
				}
			}, this.particleSpawnRate);

			// Start cleanup interval to remove completed particles
			this.cleanupInterval = setInterval(() => {
				this.cleanupCompletedParticles();

				// Only handle duration logic for non-fireworks animations
				// Fireworks animations handle their own duration via CanvasFireworksRenderer
				if (this.animationType !== 'fireworks') {
					// Check if we should start winding down based on duration
					if (this.options.animationDuration > 0 && !this.isWindingDown) {
						const elapsed = (Date.now() - this.animationStartTime) / 1000;
						if (elapsed >= this.options.animationDuration) {
							// Start winding down - stop spawning new particles
							this.isWindingDown = true;
							if (this.particleSpawnInterval) {
								clearInterval(this.particleSpawnInterval);
								this.particleSpawnInterval = null;
							}
						}
					}

					// If winding down and no particles left, stop completely
					if (this.isWindingDown && this.particles.length === 0) {
						this.stopAnimation();
					}
				}
			}, 1000); // Clean up every second

			this.animate();
		}

		/**
		 * Start confetti animation (legacy method for backward compatibility)
		 */
		startConfetti() {
			this.animationType = 'confetti';
			this.startAnimation();
		}

		/**
		 * Start continuous confetti animation (legacy method for backward compatibility)
		 */
		startContinuousConfetti() {
			this.animationType = 'confetti';
			this.startContinuousAnimation();
		}

		/**
		 * Create particles (legacy method)
		 */
		createParticles() {
			const particleCount = 100;
			for (let i = 0; i < particleCount; i++) {
				this.spawnParticle();
			}
		}

		/**
		 * Create confetti particles (legacy method)
		 */
		createConfettiParticles() {
			this.createParticles();
		}

		/**
		 * Spawn initial batch of particles for continuous animation
		 */
		spawnParticlesBatch() {
			const initialCount = Math.min(60, this.maxActiveParticles);
			for (let i = 0; i < initialCount; i++) {
				this.spawnParticle();
			}
		}

		/**
		 * Spawn a single particle based on animation type
		 */
		spawnParticle() {
			// Handle fireworks separately as it uses canvas
			if (this.animationType === 'fireworks') {
				if (!this.canvasRenderer) {
					const canvas = this.animationContainer.querySelector('.fireworks-canvas');
					if (canvas) {
						this.canvasRenderer = new CanvasFireworksRenderer(canvas, this.animationContainer, this.options.animationDuration, this);
						this.canvasRenderer.start();
					}
				}
				return; // Canvas handles its own particle management
			}

			if (this.particles.length >= this.maxActiveParticles) return;

			const particle = document.createElement('div');

			// Set particle properties based on animation type
			switch (this.animationType) {
				case 'confetti':
				default:
					this.createConfettiParticle(particle);
					break;
			}

			// Store creation time and duration for cleanup
			const animationDelay = parseFloat(particle.style.animationDelay) || 0;
			const animationDuration = parseFloat(particle.style.animationDuration) || 6;
			particle.dataset.createdAt = Date.now();
			particle.dataset.duration = (animationDelay + animationDuration) * 1000;

			this.animationContainer.appendChild(particle);
			this.particles.push(particle);
		}

		/**
		 * Create a confetti particle
		 */
		createConfettiParticle(particle) {
			const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3'];
			particle.className = 'confetti-particle animation-particle';

			const color = colors[Math.floor(Math.random() * colors.length)];
			const size = Math.random() * 8 + 4; // 4-12px
			const startX = Math.random() * 100; // 0-100%
			const rotation = Math.random() * 360;
			const animationDelay = Math.random() * 0.5; // 0-0.5s delay
			const animationDuration = Math.random() * 4 + 6; // 6-10s duration

			particle.style.cssText = `
				background-color: ${color};
				width: ${size}px;
				height: ${size}px;
				left: ${startX}%;
				transform: rotate(${rotation}deg);
				animation-delay: ${animationDelay}s;
				animation-duration: ${animationDuration}s;
			`;
		}

		/**
		 * Create a fireworks particle (canvas-based)
		 */
		createFireworksParticle(particle) {
			// For fireworks, we don't create DOM particles
			// Instead we'll initialize the canvas renderer
			if (!this.canvasRenderer) {
				const canvas = this.animationContainer.querySelector('.fireworks-canvas');
				if (canvas) {
					this.canvasRenderer = new CanvasFireworksRenderer(canvas, this.animationContainer, this.options.animationDuration);
					this.canvasRenderer.start();
				}
			}
			// Remove the particle from DOM-based tracking as canvas handles its own particles
			return null;
		}





		/**
		 * Clean up particles that have completed their animation
		 */
		cleanupCompletedParticles() {
			const now = Date.now();
			const particlesToRemove = [];

			this.particles.forEach((particle, index) => {
				const createdAt = parseInt(particle.dataset.createdAt);
				const duration = parseInt(particle.dataset.duration);

				// Check if particle animation is complete (with small buffer)
				if (now - createdAt > duration + 500) {
					particle.remove();
					particlesToRemove.push(index);
				}
			});

			// Remove from tracking array (reverse order to maintain indices)
			particlesToRemove.reverse().forEach(index => {
				this.particles.splice(index, 1);
			});
		}

		/**
		 * Animate particles
		 */
		animate() {
			if (!this.isAnimating) {
				return;
			}

			// CSS animations handle the particle movement
			// This method can be extended for more complex animations if needed

			this.animationId = requestAnimationFrame(() => this.animate());
		}

		/**
		 * Animate confetti particles (legacy method for backward compatibility)
		 */
		animateConfetti() {
			this.animate();
		}

		/**
		 * Stop animation and clean up
		 */
		stopAnimation() {
			// Prevent infinite recursion
			if (this.isStopping) {
				return;
			}
			this.isStopping = true;

			this.isAnimating = false;
			this.isWindingDown = false;

			// Stop canvas renderer if it exists
			if (this.canvasRenderer) {
				this.canvasRenderer.stop();
				this.canvasRenderer = null;
			}

			// Clear intervals
			if (this.animationId) {
				cancelAnimationFrame(this.animationId);
				this.animationId = null;
			}

			if (this.particleSpawnInterval) {
				clearInterval(this.particleSpawnInterval);
				this.particleSpawnInterval = null;
			}

			if (this.cleanupInterval) {
				clearInterval(this.cleanupInterval);
				this.cleanupInterval = null;
			}

			// Remove particles after fade out
			setTimeout(() => {
				this.particles.forEach(particle => {
					if (particle.parentNode) {
						particle.parentNode.removeChild(particle);
					}
				});
				this.particles = [];
				// Reset the stopping flag after cleanup
				this.isStopping = false;
			}, 1000);
		}

		/**
		 * Stop confetti animation and clean up (legacy method for backward compatibility)
		 */
		stopConfetti() {
			this.stopAnimation();
		}

	}

// Auto-initialize on DOM ready
	document.addEventListener('DOMContentLoaded', () => {
		const transitionWidgets = document.querySelectorAll('.agend-loyalty-points-available-card.is-animated');

		transitionWidgets.forEach(widget => {
			const pointsData = widget.querySelector('.agend-loyalty-points-available-points-counter');
			if (pointsData) {
				const targetPoints = parseInt(pointsData.dataset.targetPoints) || 0;
				const animationType = widget.dataset.animationType || 'confetti';
				const animationDuration = parseInt(widget.dataset.animationDuration) || 0;
				const animation = new LoyaltyPointsAnimation();
				animation.init(widget, {
					targetPoints,
					animationType,
					animationDuration,
					continuousAnimation: true
				});
			}
		});
	});

})();
